/**
 * Lightweight CTA tracking.
 * Pushes events to window.dataLayer (GTM/GA4-compatible) and gtag if present.
 * Safe to call in SSR or when no analytics are wired up — silent no-op.
 */
type CTAProps = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Conversion event — fires on form success / booking / phone clicks.
 * Routes to GA4 (gtag), Meta Pixel (fbq), dataLayer (GTM), and internal
 * trackEvent so the /funnel dashboard picks it up. All destinations are
 * fire-and-forget; missing pixels are silent no-ops.
 */
export type ConversionName =
  | "lead_magnet_success"
  | "masterclass_register_submit"
  | "inquiry_submit"
  | "strategy_session_submit"
  | "masterclass_conversion"
  | "scan_complete"
  | "booking_complete"
  | "fsap_consult_submit"
  | "fsap_intake_submit"
  | "compassion_apply_submit";

export function trackConversion(
  name: ConversionName,
  props: CTAProps = {},
) {
  if (typeof window === "undefined") return;
  const payload = { event: name, ts: Date.now(), path: window.location.pathname, ...props };
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    // GA4 — standard generate_lead event plus a custom-named event for filtering.
    window.gtag?.("event", "generate_lead", { ...payload, currency: "USD", value: 0 });
    window.gtag?.("event", name, payload);
    // Meta Pixel — standard Lead event.
    window.fbq?.("track", "Lead", { content_name: name, ...props });
    window.dispatchEvent(new CustomEvent("conversion:tracked", { detail: { name, props } }));
    if (import.meta.env.DEV) console.debug("[conv]", name, props);
  } catch {
    /* never let analytics break UX */
  }
}

/**
 * Placement-level CTA conversion event.
 *
 * Distinct from `trackConversion` (which fires on a COMPLETED lead): this is
 * the intent-level conversion signal ad platforms optimize on — the moment a
 * visitor clicks the Register / Strategy CTA at a given placement. It emits a
 * single consistently named event (`cta_conversion`) with `placement` as a
 * dimension so hero vs mid_page vs final can be compared directly.
 */
export function trackCTAConversion(name: string, props: CTAProps = {}) {
  if (typeof window === "undefined") return;
  const payload = {
    event: "cta_conversion",
    conversion: true,
    conversion_name: name,
    path: window.location.pathname,
    ts: Date.now(),
    ...props,
  };
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.gtag?.("event", "cta_conversion", payload);
    window.fbq?.("track", "InitiateCheckout", { content_name: name, ...props });
    window.dispatchEvent(
      new CustomEvent("cta:conversion", { detail: { name, props: payload } }),
    );
    if (import.meta.env.DEV) console.debug("[cta-conv]", name, payload);
  } catch {
    /* never let analytics break UX */
  }
}

export function trackCTA(name: string, props: CTAProps = {}) {
  if (typeof window === "undefined") return;
  const payload = {
    event: "cta_click",
    cta_name: name,
    cta_path: window.location.pathname,
    cta_ts: Date.now(),
    ...props,
  };
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.gtag?.("event", "cta_click", payload);
    window.dispatchEvent(new CustomEvent("cta:tracked", { detail: { name, props } }));
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[cta]", name, props);
    }
  } catch {
    // never let analytics break UX
  }
}

/** Convenience helper for onClick handlers on <a>/<button>. */
export const onCTA = (name: string, props?: CTAProps) => () => trackCTA(name, props);

/** Generic event — analytics-agnostic. Used by site-wide capture hook. */
export function trackEvent(name: string, props: CTAProps = {}) {
  if (typeof window === "undefined") return;
  const payload = { event: name, ts: Date.now(), path: window.location.pathname, ...props };
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.gtag?.("event", name, payload);
    if (import.meta.env.DEV) console.debug("[evt]", name, props);
  } catch {
    /* noop */
  }
}

/** Page view event — fires on every SPA route change. */
export function trackPageView(path: string) {
  trackEvent("page_view", { page_path: path, page_title: document.title });
}
